import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import styles from './GuidanceOverview.module.scss';
import { formatRoundFixed, formatLarge } from '@benzinga/utils';
import type { Guidance } from '@benzinga/calendar-manager';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import type { GuidanceSummary } from '../../utils/quote/summaries';

interface GuidanceOverviewProps {
  guidanceSummary: GuidanceSummary | null;
  quote: DelayedQuote | null;
}

const formatDate = (date: string, language: string): string => {
  return DateTime.fromISO(date).setLocale(language).toFormat('MMMM d, yyyy');
};

const formatEpsRange = (min: string, max: string): string => {
  return `$${formatRoundFixed(Number(min), 2)} - $${formatRoundFixed(Number(max), 2)}`;
};

const formatRevenueRange = (min: string, max: string): string => {
  const minVal = Number(min);
  const maxVal = Number(max);
  return `$${formatLarge(minVal)} - $${formatLarge(maxVal)}`;
};

const determineGuidanceDirection = (
  current: { min: string; max: string },
  prior: { min: string; max: string },
): 'raised' | 'lowered' | 'maintained' | null => {
  if (!prior.min || !prior.max || !current.min || !current.max) return null;

  const currentMid = (Number(current.min) + Number(current.max)) / 2;
  const priorMid = (Number(prior.min) + Number(prior.max)) / 2;

  if (priorMid === 0) return null;

  const percentChange = ((currentMid - priorMid) / Math.abs(priorMid)) * 100;

  if (percentChange > 1) return 'raised';
  if (percentChange < -1) return 'lowered';
  return 'maintained';
};

export const GuidanceOverview: React.FC<GuidanceOverviewProps> = ({ guidanceSummary, quote }) => {
  const { t, i18n } = useTranslation('quote');

  if (!quote || !guidanceSummary) {
    return null;
  }

  const { last, guidance } = guidanceSummary;
  const companyName = quote.name || quote.symbol;
  const symbol = quote.symbol;

  // Include guidance entries that have either EPS or revenue data
  const guidanceWithData = guidance.filter(
    (g: Guidance) =>
      (!!g.eps_guidance_min && !!g.eps_guidance_max) || (!!g.revenue_guidance_min && !!g.revenue_guidance_max),
  );

  const renderLatestGuidanceParagraph = () => {
    if (!last || (!last.eps_guidance_min && !last.revenue_guidance_min)) {
      return null;
    }

    const hasEps = last.eps_guidance_min && last.eps_guidance_max;
    const hasRevenue = last.revenue_guidance_min && last.revenue_guidance_max;
    const period = last.period === 'FY' ? 'fiscal year' : last.period;

    let translationKey: string;
    if (hasEps && hasRevenue) {
      translationKey = 'Quote.Guidance.Overview.latest-guidance-eps-and-revenue';
    } else if (hasEps) {
      translationKey = 'Quote.Guidance.Overview.latest-guidance-eps-only';
    } else {
      translationKey = 'Quote.Guidance.Overview.latest-guidance-revenue-only';
    }

    return (
      <p>
        {t(translationKey, {
          companyName,
          symbol,
          date: formatDate(last.date, i18n.language),
          period,
          periodYear: last.period_year,
          epsRange: hasEps ? formatEpsRange(last.eps_guidance_min, last.eps_guidance_max) : undefined,
          epsEstimate: last.eps_guidance_est ? `$${formatRoundFixed(Number(last.eps_guidance_est), 2)}` : undefined,
          revenueRange: hasRevenue
            ? formatRevenueRange(last.revenue_guidance_min, last.revenue_guidance_max)
            : undefined,
        })}
      </p>
    );
  };

  const renderGuidanceComparisonParagraph = () => {
    if (!last) return null;

    const hasEpsPrior =
      last.eps_guidance_prior_min && last.eps_guidance_prior_max && last.eps_guidance_min && last.eps_guidance_max;
    const hasRevenuePrior =
      last.revenue_guidance_prior_min &&
      last.revenue_guidance_prior_max &&
      last.revenue_guidance_min &&
      last.revenue_guidance_max;

    // If the latest entry has explicit prior fields, use those
    if (hasEpsPrior || hasRevenuePrior) {
      const epsDirection = hasEpsPrior
        ? determineGuidanceDirection(
            { min: last.eps_guidance_min, max: last.eps_guidance_max },
            { min: last.eps_guidance_prior_min, max: last.eps_guidance_prior_max },
          )
        : null;

      const revenueDirection = hasRevenuePrior
        ? determineGuidanceDirection(
            { min: last.revenue_guidance_min, max: last.revenue_guidance_max },
            { min: last.revenue_guidance_prior_min, max: last.revenue_guidance_prior_max },
          )
        : null;

      if (!epsDirection && !revenueDirection) return null;

      let translationKey: string;
      if (epsDirection && revenueDirection) {
        translationKey = 'Quote.Guidance.Overview.comparison-eps-and-revenue';
      } else if (epsDirection) {
        translationKey = 'Quote.Guidance.Overview.comparison-eps-only';
      } else {
        translationKey = 'Quote.Guidance.Overview.comparison-revenue-only';
      }

      return (
        <p>
          {t(translationKey, {
            companyName,
            epsDirection: epsDirection ? t(`Quote.Guidance.Overview.direction-${epsDirection}`) : undefined,
            epsPriorRange: hasEpsPrior
              ? formatEpsRange(last.eps_guidance_prior_min, last.eps_guidance_prior_max)
              : undefined,
            revenueDirection: revenueDirection ? t(`Quote.Guidance.Overview.direction-${revenueDirection}`) : undefined,
            revenuePriorRange: hasRevenuePrior
              ? formatRevenueRange(last.revenue_guidance_prior_min, last.revenue_guidance_prior_max)
              : undefined,
          })}
        </p>
      );
    }

    // Fall back to sequential comparison: compare latest entry to the previous entry
    if (guidanceWithData.length >= 2) {
      const current = guidanceWithData[0];
      const previous = guidanceWithData[1];

      const currentHasRevenue = current.revenue_guidance_min && current.revenue_guidance_max;
      const previousHasRevenue = previous.revenue_guidance_min && previous.revenue_guidance_max;
      const currentHasEps = current.eps_guidance_min && current.eps_guidance_max;
      const previousHasEps = previous.eps_guidance_min && previous.eps_guidance_max;

      if (currentHasRevenue && previousHasRevenue) {
        const direction = determineGuidanceDirection(
          { min: current.revenue_guidance_min, max: current.revenue_guidance_max },
          { min: previous.revenue_guidance_min, max: previous.revenue_guidance_max },
        );
        if (!direction) return null;

        return (
          <p>
            {t('Quote.Guidance.Overview.comparison-revenue-only', {
              companyName,
              revenueDirection: t(`Quote.Guidance.Overview.direction-${direction}`),
              revenuePriorRange: formatRevenueRange(previous.revenue_guidance_min, previous.revenue_guidance_max),
            })}
          </p>
        );
      }

      if (currentHasEps && previousHasEps) {
        const direction = determineGuidanceDirection(
          { min: current.eps_guidance_min, max: current.eps_guidance_max },
          { min: previous.eps_guidance_min, max: previous.eps_guidance_max },
        );
        if (!direction) return null;

        return (
          <p>
            {t('Quote.Guidance.Overview.comparison-eps-only', {
              companyName,
              epsDirection: t(`Quote.Guidance.Overview.direction-${direction}`),
              epsPriorRange: formatEpsRange(previous.eps_guidance_min, previous.eps_guidance_max),
            })}
          </p>
        );
      }
    }

    return null;
  };

  const renderHistoryParagraph = () => {
    if (guidanceWithData.length < 2) return null;

    const totalEntries = guidanceWithData.length;
    const oldestDate = guidanceWithData[guidanceWithData.length - 1]?.date;
    const newestDate = guidanceWithData[0]?.date;

    if (!oldestDate || !newestDate) return null;

    // Count how many times guidance was raised vs lowered
    // Always use sequential quarter-over-quarter comparison for consistent counting
    let raisedCount = 0;
    let loweredCount = 0;

    for (let i = 0; i < guidanceWithData.length - 1; i++) {
      const current = guidanceWithData[i];
      const previous = guidanceWithData[i + 1];

      // If the current entry has explicit prior fields, use those
      const hasEpsPrior =
        current.eps_guidance_prior_min &&
        current.eps_guidance_prior_max &&
        current.eps_guidance_min &&
        current.eps_guidance_max;
      const hasRevenuePrior =
        current.revenue_guidance_prior_min &&
        current.revenue_guidance_prior_max &&
        current.revenue_guidance_min &&
        current.revenue_guidance_max;

      if (hasEpsPrior) {
        const direction = determineGuidanceDirection(
          { min: current.eps_guidance_min, max: current.eps_guidance_max },
          { min: current.eps_guidance_prior_min, max: current.eps_guidance_prior_max },
        );
        if (direction === 'raised') raisedCount++;
        if (direction === 'lowered') loweredCount++;
      } else if (hasRevenuePrior) {
        const direction = determineGuidanceDirection(
          { min: current.revenue_guidance_min, max: current.revenue_guidance_max },
          { min: current.revenue_guidance_prior_min, max: current.revenue_guidance_prior_max },
        );
        if (direction === 'raised') raisedCount++;
        if (direction === 'lowered') loweredCount++;
      } else {
        // No explicit priors — compare to previous entry sequentially
        const currentHasRevenue = current.revenue_guidance_min && current.revenue_guidance_max;
        const previousHasRevenue = previous.revenue_guidance_min && previous.revenue_guidance_max;
        const currentHasEps = current.eps_guidance_min && current.eps_guidance_max;
        const previousHasEps = previous.eps_guidance_min && previous.eps_guidance_max;

        if (currentHasRevenue && previousHasRevenue) {
          const direction = determineGuidanceDirection(
            { min: current.revenue_guidance_min, max: current.revenue_guidance_max },
            { min: previous.revenue_guidance_min, max: previous.revenue_guidance_max },
          );
          if (direction === 'raised') raisedCount++;
          if (direction === 'lowered') loweredCount++;
        } else if (currentHasEps && previousHasEps) {
          const direction = determineGuidanceDirection(
            { min: current.eps_guidance_min, max: current.eps_guidance_max },
            { min: previous.eps_guidance_min, max: previous.eps_guidance_max },
          );
          if (direction === 'raised') raisedCount++;
          if (direction === 'lowered') loweredCount++;
        }
      }
    }

    // Build a natural-sounding direction summary
    let directionSummary: string;
    if (raisedCount > 0 && loweredCount > 0) {
      directionSummary = `During this period, the company raised its outlook ${raisedCount} ${raisedCount === 1 ? 'time' : 'times'} and lowered it ${loweredCount} ${loweredCount === 1 ? 'time' : 'times'}, providing investors with insight into management's evolving expectations.`;
    } else if (raisedCount > 0) {
      directionSummary = `During this period, the company raised its outlook ${raisedCount} ${raisedCount === 1 ? 'time' : 'times'}, reflecting management's growing confidence in business performance.`;
    } else if (loweredCount > 0) {
      directionSummary = `During this period, the company lowered its outlook ${loweredCount} ${loweredCount === 1 ? 'time' : 'times'}, signaling management's revised expectations.`;
    } else {
      directionSummary = "This history provides investors with insight into management's expectations over time.";
    }

    return (
      <p>
        {t('Quote.Guidance.Overview.history', {
          companyName,
          totalEntries,
          oldestDate: formatDate(oldestDate, i18n.language),
          newestDate: formatDate(newestDate, i18n.language),
          directionSummary,
        })}
      </p>
    );
  };

  const hasContent = (last && (last.eps_guidance_min || last.revenue_guidance_min)) || guidanceWithData.length >= 2;

  if (!hasContent) {
    return null;
  }

  return (
    <div className={styles.overviewContainer}>
      {renderLatestGuidanceParagraph()}
      {renderGuidanceComparisonParagraph()}
      {renderHistoryParagraph()}
    </div>
  );
};


export default GuidanceOverview;
